import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Pencil, ExternalLink } from "lucide-react";
import { api, apiError, gbp, imgUrl, miles } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";
import { InstagramPostButton } from "./InstagramPost";
import { CopyCaptionButton } from "./InstagramCaption";
import { DownloadPhotosButton } from "./DownloadPhotos";

export const STATUSES = [
  ["draft", "Draft"], ["available", "Available"], ["just_arrived", "Just arrived"], ["coming_soon", "Arriving soon"], ["reserved", "Reserved"], ["sold", "Sold"],
];

export default function AdminVehicles() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/vehicles").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const patch = async (v, data, msg) => {
    try { await api.patch(`/admin/vehicles/${v.id}`, data); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const changePrice = (v) => {
    const p = window.prompt(`New price for ${v.make} ${v.model} (£)`, v.price ?? "");
    if (p && !Number.isNaN(+p)) patch(v, { price: +p, previous_price: v.price }, "Price updated");
  };
  const rows = (list || []).filter((v) => `${v.make} ${v.model} ${v.derivative} ${v.registration_number} ${v.vehicle_id}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-vehicles">
      <AdminTitle title="Vehicles">
        <input className="field !w-56 !py-2.5 text-sm" placeholder="Search stock…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-vehicle-search" />
        <Link to="/admin/vehicles/new" className="btn btn-primary !py-3" data-testid="admin-add-vehicle">Add vehicle</Link>
      </AdminTitle>
      <div className="overflow-x-auto border border-stone bg-paper">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="border-b border-stone text-xs uppercase tracking-[0.12em] text-charcoal/70">
            <tr><th className="p-3">Vehicle</th><th className="p-3">Reg</th><th className="p-3">Price</th><th className="p-3">Status</th><th className="p-3">Featured</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {list === null && <tr><td colSpan={6} className="p-4">Loading…</td></tr>}
            {rows.map((v) => (
              <tr key={v.id} className="border-b border-stone/60 align-middle" data-testid={`admin-vehicle-row-${v.slug}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <img src={imgUrl(v.images?.[0])} alt="" className="h-12 w-16 object-cover" />
                    <div><p className="font-medium">{v.year} {v.make} {v.model}</p><p className="text-xs text-charcoal/70">{v.derivative} · {miles(v.mileage)}</p></div>
                  </div>
                </td>
                <td className="p-3 text-xs">{v.registration_number}</td>
                <td className="p-3"><button className="underline-offset-2 hover:underline" onClick={() => changePrice(v)} data-testid={`price-${v.slug}`}>{gbp(v.price)}</button></td>
                <td className="p-3">
                  <select className="field !w-36 !py-1.5 text-xs" value={v.status} onChange={(e) => patch(v, { status: e.target.value }, "Status updated")} data-testid={`status-${v.slug}`}>
                    {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <button onClick={() => patch(v, { featured: !v.featured }, v.featured ? "Removed from featured" : "Set as featured")} aria-label="Toggle featured" data-testid={`featured-${v.slug}`}>
                    <Star className={`h-5 w-5 ${v.featured ? "fill-brass text-brass" : "text-mist"}`} />
                  </button>
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-3">
                    <a href={`/stock/${v.slug}`} target="_blank" rel="noreferrer" aria-label="View on site"><ExternalLink className="h-4 w-4" /></a>
                    <DownloadPhotosButton vehicle={v} />
                    <InstagramPostButton vehicle={v} />
                    <CopyCaptionButton vehicle={v} />
                    <Link to={`/admin/vehicles/${v.id}`} aria-label="Edit" data-testid={`edit-${v.slug}`}><Pencil className="h-4 w-4" /></Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
